import React, { useCallback, useEffect, useRef, useState } from "react";
import Cropper, { Area, MediaSize } from "react-easy-crop";
import { Modal, ModalBody, ModalFooter, Button, Spinner } from "reactstrap";
import { useIntl } from "react-intl";
import { cropImageToBlob } from "../../../cores/helpers/crop-image-to-blob";
import "react-easy-crop/react-easy-crop.css";
import "./AiImageCropModal.scss";

type AspectPreset = "original" | "1:1" | "4:5" | "3:4" | "16:9" | "9:16";

type Props = {
  isOpen: boolean;
  imageSrc: string | null;
  onClose: () => void;
  onApply: (blob: Blob) => void | Promise<void>;
  busy?: boolean;
};

export function AiImageCropModal({
  isOpen,
  imageSrc,
  onClose,
  onApply,
  busy = false,
}: Props) {
  const intl = useIntl();
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [aspectPreset, setAspectPreset] = useState<AspectPreset>("original");
  const [natural, setNatural] = useState({ w: 1, h: 1 });
  const croppedPixelsRef = useRef<Area | null>(null);
  const [applyError, setApplyError] = useState<string | null>(null);

  const aspect =
    aspectPreset === "original"
      ? natural.w / natural.h
      : aspectPreset === "1:1"
      ? 1
      : aspectPreset === "4:5"
      ? 4 / 5
      : aspectPreset === "3:4"
      ? 3 / 4
      : aspectPreset === "16:9"
      ? 16 / 9
      : 9 / 16;

  const cropObjectFit =
    aspectPreset === "original"
      ? natural.w >= natural.h
        ? "horizontal-cover"
        : "vertical-cover"
      : aspect >= 1
      ? "horizontal-cover"
      : "vertical-cover";

  useEffect(() => {
    if (!imageSrc) return;
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setAspectPreset("original");
    croppedPixelsRef.current = null;
    setApplyError(null);
  }, [imageSrc]);

  const onCropComplete = useCallback(
    (_croppedArea: Area, croppedAreaPixels: Area) => {
      croppedPixelsRef.current = croppedAreaPixels;
    },
    []
  );

  const onMediaLoaded = useCallback((mediaSize: MediaSize) => {
    setNatural({
      w: mediaSize.naturalWidth || 1,
      h: mediaSize.naturalHeight || 1,
    });
  }, []);

  const handleApply = async () => {
    if (!imageSrc || !croppedPixelsRef.current) return;
    setApplyError(null);
    try {
      const blob = await cropImageToBlob(imageSrc, croppedPixelsRef.current);
      await onApply(blob);
    } catch {
      setApplyError(intl.formatMessage({ id: "app.aiGeneration.cropError" }));
    }
  };

  const handleClose = () => {
    if (busy) return;
    setApplyError(null);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen && !!imageSrc}
      toggle={handleClose}
      className="ai-image-crop-modal"
      centered
      backdrop="static"
      fade={false}
      zIndex={2100}
    >
      <div className="modal-header">
        <h5 className="modal-title">
          {intl.formatMessage({ id: "app.aiGeneration.cropModalTitle" })}
        </h5>
        <button
          type="button"
          className="close"
          onClick={handleClose}
          disabled={busy}
          aria-label={intl.formatMessage({ id: "app.aiGeneration.cropCancelBtn" })}
        >
          <span aria-hidden>&times;</span>
        </button>
      </div>
      <ModalBody className="ai-image-crop-modal__body">
        {imageSrc && (
          <>
            <div className="ai-image-crop-modal__toolbar">
              <span className="ai-image-crop-modal__toolbar-label">
                {intl.formatMessage({ id: "app.aiGeneration.cropAspectLabel" })}
              </span>
              {(
                [
                  ["original", "app.aiGeneration.cropAspectOriginal"],
                  ["1:1", "app.aiGeneration.cropAspect1_1"],
                  ["4:5", "app.aiGeneration.cropAspect4_5"],
                  ["3:4", "app.aiGeneration.cropAspect3_4"],
                  ["16:9", "app.aiGeneration.cropAspect16_9"],
                  ["9:16", "app.aiGeneration.cropAspect9_16"],
                ] as const
              ).map(([key, msgId]) => (
                <button
                  key={key}
                  type="button"
                  className={
                    "ai-image-crop-modal__aspect-btn" +
                    (aspectPreset === key ? " ai-image-crop-modal__aspect-btn--active" : "")
                  }
                  onClick={() => setAspectPreset(key as AspectPreset)}
                  disabled={busy}
                >
                  {intl.formatMessage({ id: msgId })}
                </button>
              ))}
            </div>
            <div className="ai-image-crop-modal__crop-wrap">
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={aspect}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                onMediaLoaded={onMediaLoaded}
                showGrid
                objectFit={cropObjectFit}
              />
            </div>
            <div className="ai-image-crop-modal__zoom">
              <span className="ai-image-crop-modal__toolbar-label">
                {intl.formatMessage({ id: "app.aiGeneration.cropZoomLabel" })}
              </span>
              <input
                type="range"
                min={1}
                max={3}
                step={0.05}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                disabled={busy}
                aria-label={intl.formatMessage({
                  id: "app.aiGeneration.cropZoomLabel",
                })}
              />
            </div>
            {applyError && (
              <p className="text-danger small mb-0 mt-2">{applyError}</p>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter className="ai-image-crop-modal__footer">
        <Button color="secondary" outline onClick={handleClose} disabled={busy}>
          {intl.formatMessage({ id: "app.aiGeneration.cropCancelBtn" })}
        </Button>
        <Button
          color="primary"
          onClick={handleApply}
          disabled={busy || !imageSrc}
        >
          {busy ? (
            <>
              <Spinner size="sm" className="mr-2" />{" "}
              {intl.formatMessage({ id: "app.aiGeneration.cropApplying" })}
            </>
          ) : (
            intl.formatMessage({ id: "app.aiGeneration.cropApplyBtn" })
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
